// AssetDossierClassic.js - Asset Dossier Variation A: "Classic Dossier"
// Tab-based sections with formal document feel

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getAsset, getAssetScanDetails } from '../../services/assetService';
import { formatRelativeTime, getSeverityColor } from '../../services/commandCenterService';
import { SkeletonDashboard } from '../../components/common';
import './AssetDossierClassic.css';

// Tab navigation component
function TabNav({ tabs, activeTab, onTabChange }) {
    return (
        <nav className="adc-tabs">
            {tabs.map(tab => (
                <button
                    key={tab.id}
                    className={`adc-tab ${activeTab === tab.id ? 'active' : ''}`}
                    onClick={() => onTabChange(tab.id)}
                >
                    <span className="tab-icon">{tab.icon}</span>
                    <span className="tab-label">{tab.label}</span>
                    {tab.count > 0 && (
                        <span className="tab-count">{tab.count}</span>
                    )}
                </button>
            ))}
        </nav>
    );
}

// Target profile header
function TargetProfile({ asset }) {
    const getRiskClass = (score) => {
        if (score >= 80) return 'risk-healthy';
        if (score >= 60) return 'risk-caution';
        if (score >= 40) return 'risk-atrisk';
        return 'risk-critical';
    };

    return (
        <header className="adc-profile">
            <div className="profile-main">
                <div className="profile-icon">
                    {asset.assetType === 'domain' ? '🌐' :
                     asset.assetType === 'ip' ? '🖥️' : '🔗'}
                </div>
                <div className="profile-identity">
                    <h1 className="profile-value">{asset.value}</h1>
                    <span className="profile-type">{asset.assetType?.toUpperCase()}</span>
                </div>
                <div className={`profile-risk ${getRiskClass(asset.riskScore)}`}>
                    <span className="risk-score">{asset.riskScore || 0}</span>
                    <span className="risk-label">Risk Score</span>
                </div>
            </div>

            <div className="profile-meta">
                <div className="meta-item">
                    <span className="meta-label">Status</span>
                    <span className={`meta-value status-${asset.status || 'active'}`}>
                        {asset.status || 'Active'}
                    </span>
                </div>
                <div className="meta-item">
                    <span className="meta-label">Last Scan</span>
                    <span className="meta-value">{formatRelativeTime(asset.lastScanAt)}</span>
                </div>
                <div className="meta-item">
                    <span className="meta-label">Created</span>
                    <span className="meta-value">{formatRelativeTime(asset.createdAt)}</span>
                </div>
                {asset.assetType === 'domain' && (
                    <>
                        <div className="meta-item">
                            <span className="meta-label">Subdomains</span>
                            <span className="meta-value">{asset.liveSubdomainCount || 0}</span>
                        </div>
                        <div className="meta-item">
                            <span className="meta-label">IPs</span>
                            <span className="meta-value">{asset.ipCount || 0}</span>
                        </div>
                    </>
                )}
                <div className="meta-item">
                    <span className="meta-label">Open Ports</span>
                    <span className="meta-value">{asset.openPortCount || 0}</span>
                </div>
            </div>
        </header>
    );
}

// Security Summary Tab
function SecuritySummaryTab({ asset, vulnCounts }) {
    const getTrendIcon = (trend) => {
        if (trend === 'improving') return '↑';
        if (trend === 'worsening') return '↓';
        return '→';
    };

    return (
        <div className="adc-tab-content">
            <section className="content-section">
                <h2>Security Overview</h2>

                <div className="summary-grid">
                    <div className="summary-card risk-overview">
                        <h3>Risk Assessment</h3>
                        <div className="risk-display">
                            <div className="risk-gauge">
                                <span className="gauge-score">{asset.riskScore || 0}</span>
                                <span className="gauge-max">/100</span>
                            </div>
                            <div className="risk-trend">
                                <span className={`trend-indicator trend-${asset.trend || 'stable'}`}>
                                    {getTrendIcon(asset.trend)} {asset.trend || 'Stable'}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="summary-card findings-breakdown">
                        <h3>Findings by Severity</h3>
                        <div className="findings-bars">
                            <div className="finding-bar">
                                <span className="bar-label critical">Critical</span>
                                <div className="bar-track">
                                    <div
                                        className="bar-fill critical"
                                        style={{ width: `${Math.min((vulnCounts.critical / Math.max(vulnCounts.total, 1)) * 100, 100)}%` }}
                                    />
                                </div>
                                <span className="bar-count">{vulnCounts.critical}</span>
                            </div>
                            <div className="finding-bar">
                                <span className="bar-label high">High</span>
                                <div className="bar-track">
                                    <div
                                        className="bar-fill high"
                                        style={{ width: `${Math.min((vulnCounts.high / Math.max(vulnCounts.total, 1)) * 100, 100)}%` }}
                                    />
                                </div>
                                <span className="bar-count">{vulnCounts.high}</span>
                            </div>
                            <div className="finding-bar">
                                <span className="bar-label medium">Medium</span>
                                <div className="bar-track">
                                    <div
                                        className="bar-fill medium"
                                        style={{ width: `${Math.min((vulnCounts.medium / Math.max(vulnCounts.total, 1)) * 100, 100)}%` }}
                                    />
                                </div>
                                <span className="bar-count">{vulnCounts.medium}</span>
                            </div>
                            <div className="finding-bar">
                                <span className="bar-label low">Low</span>
                                <div className="bar-track">
                                    <div
                                        className="bar-fill low"
                                        style={{ width: `${Math.min((vulnCounts.low / Math.max(vulnCounts.total, 1)) * 100, 100)}%` }}
                                    />
                                </div>
                                <span className="bar-count">{vulnCounts.low}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

// Attack Surface Tab
function AttackSurfaceTab({ asset, scanDetails }) {
    const stageData = useMemo(() => {
        if (!scanDetails?.fullResult) return {};
        const fr = scanDetails.fullResult;
        const discoveryData = fr.discoveryData || {};
        const stageResults = fr.stageResults || {};

        return {
            subdomains: discoveryData.subdomains || stageResults.dnsEnumeration?.subdomains || [],
            ips: discoveryData.ips || stageResults.ipResolution?.ips || [],
            ports: discoveryData.ports || stageResults.portScanning?.openPorts || [],
            services: discoveryData.services || stageResults.portScanning?.services || [],
            urls: discoveryData.urls || stageResults.webCrawling?.liveUrls || [],
            technologies: discoveryData.technologies || stageResults.webCrawling?.technologies || []
        };
    }, [scanDetails]);

    return (
        <div className="adc-tab-content">
            <section className="content-section">
                <h2>Attack Surface</h2>

                <div className="surface-stats">
                    <div className="surface-stat">
                        <span className="stat-value">{stageData.subdomains?.length || 0}</span>
                        <span className="stat-label">Subdomains</span>
                    </div>
                    <div className="surface-stat">
                        <span className="stat-value">{stageData.ips?.length || 0}</span>
                        <span className="stat-label">IP Addresses</span>
                    </div>
                    <div className="surface-stat">
                        <span className="stat-value">{stageData.ports?.length || 0}</span>
                        <span className="stat-label">Open Ports</span>
                    </div>
                    <div className="surface-stat">
                        <span className="stat-value">{stageData.urls?.length || 0}</span>
                        <span className="stat-label">URLs</span>
                    </div>
                </div>

                {stageData.technologies?.length > 0 && (
                    <div className="tech-section">
                        <h3>Technologies Detected</h3>
                        <div className="tech-list">
                            {stageData.technologies.slice(0, 10).map((tech, i) => (
                                <span key={i} className="tech-badge">
                                    {typeof tech === 'string' ? tech : tech.name || tech.technology}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {stageData.services?.length > 0 && (
                    <div className="services-section">
                        <h3>Services</h3>
                        <table className="adc-table">
                            <thead>
                                <tr>
                                    <th>Port</th>
                                    <th>Service</th>
                                    <th>Version</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stageData.services.slice(0, 10).map((svc, i) => (
                                    <tr key={i}>
                                        <td>{svc.port || '-'}</td>
                                        <td>{svc.service || svc.name || '-'}</td>
                                        <td>{svc.version || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
}

// Vulnerabilities Tab
function VulnerabilitiesTab({ scanDetails, vulnFilter, setVulnFilter }) {
    const vulnerabilities = useMemo(() => {
        if (!scanDetails?.fullResult) return [];
        const fr = scanDetails.fullResult;
        return fr.findings || fr.stageResults?.vulnScanning?.findings || [];
    }, [scanDetails]);

    const filteredVulns = useMemo(() => {
        if (vulnFilter === 'all') return vulnerabilities;
        return vulnerabilities.filter(v =>
            (v.severity || v.priority || '').toLowerCase() === vulnFilter
        );
    }, [vulnerabilities, vulnFilter]);

    return (
        <div className="adc-tab-content">
            <section className="content-section">
                <div className="section-header">
                    <h2>Vulnerabilities ({filteredVulns.length})</h2>
                    <div className="filter-group">
                        <select
                            value={vulnFilter}
                            onChange={(e) => setVulnFilter(e.target.value)}
                            className="severity-filter"
                        >
                            <option value="all">All Severities</option>
                            <option value="critical">Critical</option>
                            <option value="high">High</option>
                            <option value="medium">Medium</option>
                            <option value="low">Low</option>
                        </select>
                    </div>
                </div>

                {filteredVulns.length > 0 ? (
                    <div className="vuln-list">
                        {filteredVulns.slice(0, 20).map((vuln, i) => (
                            <div key={i} className="vuln-item">
                                <div className="vuln-header">
                                    <span
                                        className="vuln-severity"
                                        style={{ color: getSeverityColor(vuln.severity || vuln.priority) }}
                                    >
                                        {(vuln.severity || vuln.priority || 'INFO').toUpperCase()}
                                    </span>
                                    <span className="vuln-title">{vuln.name || vuln.title}</span>
                                </div>
                                {vuln.description && (
                                    <p className="vuln-description">{vuln.description}</p>
                                )}
                                {(vuln.matchedAt || vuln.url) && (
                                    <div className="vuln-location">
                                        <span className="location-label">Location:</span>
                                        <code>{vuln.matchedAt || vuln.url}</code>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="empty-state">
                        <span className="empty-icon">✓</span>
                        <p>No vulnerabilities found for this filter</p>
                    </div>
                )}
            </section>
        </div>
    );
}

// Testing Approach Tab (NEW per PRD)
function TestingApproachTab({ asset, scanDetails }) {
    const generateApproach = useMemo(() => {
        const approaches = [];

        // Based on asset type and findings
        if (asset.assetType === 'domain') {
            approaches.push({
                phase: 'Reconnaissance',
                tasks: [
                    'Subdomain enumeration using multiple sources',
                    'DNS zone transfer attempts',
                    'Certificate transparency log analysis',
                    'Wayback machine historical analysis'
                ]
            });
        }

        approaches.push({
            phase: 'Service Enumeration',
            tasks: [
                'Full port scan (TCP/UDP)',
                'Service version detection',
                'Banner grabbing',
                'SSL/TLS configuration analysis'
            ]
        });

        approaches.push({
            phase: 'Vulnerability Assessment',
            tasks: [
                'Automated vulnerability scanning',
                'Manual verification of findings',
                'Default credential testing',
                'Configuration review'
            ]
        });

        approaches.push({
            phase: 'Exploitation',
            tasks: [
                'Attempt exploitation of confirmed vulnerabilities',
                'Privilege escalation testing',
                'Lateral movement assessment',
                'Data exfiltration testing'
            ]
        });

        return approaches;
    }, [asset]);

    return (
        <div className="adc-tab-content">
            <section className="content-section">
                <h2>Penetration Testing Approach</h2>
                <p className="section-intro">
                    Recommended methodology for testing this target based on discovered attack surface.
                </p>

                <div className="approach-phases">
                    {generateApproach.map((phase, i) => (
                        <div key={i} className="approach-phase">
                            <div className="phase-header">
                                <span className="phase-number">{i + 1}</span>
                                <h3>{phase.phase}</h3>
                            </div>
                            <ul className="phase-tasks">
                                {phase.tasks.map((task, j) => (
                                    <li key={j}>{task}</li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}

// Defense Recommendations Tab (NEW per PRD)
function DefenseTab({ asset, vulnCounts }) {
    const recommendations = useMemo(() => {
        const recs = [];

        if (vulnCounts.critical > 0) {
            recs.push({
                priority: 'Immediate',
                title: 'Address Critical Vulnerabilities',
                description: `${vulnCounts.critical} critical vulnerabilities require immediate remediation. These represent the highest risk to your organization.`,
                effort: 'High'
            });
        }

        if (vulnCounts.high > 0) {
            recs.push({
                priority: 'Urgent',
                title: 'Remediate High-Severity Issues',
                description: `${vulnCounts.high} high-severity issues should be addressed within the next sprint.`,
                effort: 'Medium'
            });
        }

        recs.push({
            priority: 'Standard',
            title: 'Enable Web Application Firewall',
            description: 'Deploy or configure WAF rules to protect against common web attacks.',
            effort: 'Medium'
        });

        recs.push({
            priority: 'Standard',
            title: 'Implement Security Headers',
            description: 'Add security headers (CSP, HSTS, X-Frame-Options) to all web responses.',
            effort: 'Low'
        });

        recs.push({
            priority: 'Ongoing',
            title: 'Schedule Regular Scans',
            description: 'Maintain continuous monitoring with weekly vulnerability scans.',
            effort: 'Low'
        });

        return recs;
    }, [vulnCounts]);

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'Immediate': return 'var(--severity-critical)';
            case 'Urgent': return 'var(--severity-high)';
            case 'Standard': return 'var(--severity-medium)';
            default: return 'var(--text-secondary)';
        }
    };

    return (
        <div className="adc-tab-content">
            <section className="content-section">
                <h2>Defense Recommendations</h2>
                <p className="section-intro">
                    Prioritized actions to improve the security posture of this asset.
                </p>

                <div className="recommendations-list">
                    {recommendations.map((rec, i) => (
                        <div key={i} className="recommendation-item">
                            <div className="rec-priority" style={{ borderColor: getPriorityColor(rec.priority) }}>
                                <span style={{ color: getPriorityColor(rec.priority) }}>{rec.priority}</span>
                            </div>
                            <div className="rec-content">
                                <h4>{rec.title}</h4>
                                <p>{rec.description}</p>
                                <span className="rec-effort">Effort: {rec.effort}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}

// Main Component
function AssetDossierClassic() {
    const { assetId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [asset, setAsset] = useState(null);
    const [scanDetails, setScanDetails] = useState(null);
    const [activeTab, setActiveTab] = useState('summary');
    const [vulnFilter, setVulnFilter] = useState('all');

    // Load asset data
    const loadAsset = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const result = await getAsset(assetId);
            setAsset(result.asset);

            // Load scan details if available
            if (result.asset?.lastResultId) {
                const details = await getAssetScanDetails(assetId, result.asset.lastResultId);
                setScanDetails(details);
            }
        } catch (err) {
            setError(err.message || 'Failed to load asset');
        } finally {
            setLoading(false);
        }
    }, [assetId]);

    useEffect(() => {
        loadAsset();
    }, [loadAsset]);

    // Calculate vulnerability counts
    const vulnCounts = useMemo(() => {
        if (!scanDetails?.fullResult) {
            return {
                critical: asset?.criticalCount || 0,
                high: asset?.highCount || 0,
                medium: asset?.mediumCount || 0,
                low: asset?.lowCount || 0,
                total: (asset?.criticalCount || 0) + (asset?.highCount || 0) +
                       (asset?.mediumCount || 0) + (asset?.lowCount || 0)
            };
        }

        const vulns = scanDetails.fullResult.findings || [];
        const counts = { critical: 0, high: 0, medium: 0, low: 0 };

        vulns.forEach(v => {
            const sev = (v.severity || v.priority || 'low').toLowerCase();
            if (counts[sev] !== undefined) counts[sev]++;
        });

        return { ...counts, total: vulns.length };
    }, [asset, scanDetails]);

    // Tab definitions
    const tabs = [
        { id: 'summary', label: 'Summary', icon: '📊', count: 0 },
        { id: 'surface', label: 'Attack Surface', icon: '🎯', count: 0 },
        { id: 'vulns', label: 'Vulnerabilities', icon: '⚠️', count: vulnCounts.total },
        { id: 'testing', label: 'Testing', icon: '🔬', count: 0 },
        { id: 'defense', label: 'Defense', icon: '🛡️', count: 0 }
    ];

    if (loading) {
        return (
            <div className="adc-page">
                <header className="adc-header">
                    <Link to="/dashboard-prototypes" className="back-link">← Prototypes</Link>
                    <h1>Classic Dossier</h1>
                </header>
                <SkeletonDashboard />
            </div>
        );
    }

    if (error) {
        return (
            <div className="adc-page">
                <header className="adc-header">
                    <Link to="/dashboard-prototypes" className="back-link">← Prototypes</Link>
                    <h1>Classic Dossier</h1>
                </header>
                <div className="error-state">
                    <span className="error-icon">⚠️</span>
                    <p>{error}</p>
                    <button onClick={() => navigate('/assets')}>View All Assets</button>
                </div>
            </div>
        );
    }

    return (
        <div className="adc-page">
            <header className="adc-header">
                <Link to="/dashboard-prototypes" className="back-link">← Prototypes</Link>
                <span className="header-badge">DOSSIER V1: CLASSIC</span>
            </header>

            {asset && <TargetProfile asset={asset} />}

            <TabNav tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

            <main className="adc-main">
                {activeTab === 'summary' && (
                    <SecuritySummaryTab asset={asset} vulnCounts={vulnCounts} />
                )}
                {activeTab === 'surface' && (
                    <AttackSurfaceTab asset={asset} scanDetails={scanDetails} />
                )}
                {activeTab === 'vulns' && (
                    <VulnerabilitiesTab
                        scanDetails={scanDetails}
                        vulnFilter={vulnFilter}
                        setVulnFilter={setVulnFilter}
                    />
                )}
                {activeTab === 'testing' && (
                    <TestingApproachTab asset={asset} scanDetails={scanDetails} />
                )}
                {activeTab === 'defense' && (
                    <DefenseTab asset={asset} vulnCounts={vulnCounts} />
                )}
            </main>
        </div>
    );
}

export default AssetDossierClassic;
